/**
 * Fetch wrapper with auth cookie handling and error management.
 * SDD §7.2 — lib/api.js
 *
 * All API calls include credentials (cookies) automatically.
 * On 401, the caller (useAuth hook) handles redirect to login.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    let errorBody: { error?: string; code?: string } = {};
    try {
      errorBody = await response.json();
    } catch {
      // Response may not be JSON (e.g., 302 redirects handled by fetch)
    }
    throw new ApiError(
      response.status,
      errorBody.code || 'UNKNOWN',
      errorBody.error || `Request failed with status ${response.status}`,
    );
  }

  return response.json() as Promise<T>;
}
